import {
  Arbeitsschritt,
  aufloesePikto,
  EinweisungGruppe,
  Gefaehrdung,
  LegendeEintrag,
  NotrufplanInhalt,
  Schutzmassnahme,
  SigeBlock,
  SigeProject,
} from '../components/sige-plan/types';

/**
 * Die Einweisung in den SiGe-Plan — reine Regeln, keine Datenbank, keine UI.
 *
 * WARUM hier und nicht in der Komponente: das Projekt hat keine DOM-Test-
 * umgebung (stehende Entscheidung „duenner Container, Logik in Hooks + pure
 * Funktionen"). Alles, was fachlich entscheidet — welcher Punkt auf das Blatt
 * kommt, wie lang er sein darf, ob das Ganze noch auf zwei Seiten passt —,
 * steht deshalb hier und ist ohne jsdom pruefbar.
 *
 * ─── Warum REGELN und nicht KI ─────────────────────────────────────────────
 * Die Einweisung wird auf der Baustelle gehalten, und die Baustelle ist
 * regelmaessig ohne Netz. Ein Vorschlag, der einen Gemini-Aufruf braucht, ist
 * genau dann nicht da, wenn er gebraucht wird. Die KI bleibt deshalb ein
 * spaeterer, ausdruecklich angestossener Feinschliff auf einem Vorschlag, der
 * ohne sie schon vollstaendig ist.
 *
 * ─── Warum gekappt wird ────────────────────────────────────────────────────
 * Eine Einweisung ist ein GESPRAECH, kein Vorlesen des Plans. Der Plan hat
 * hundert Gefaehrdungen; ein Blatt, das alle auffuehrt, wird nicht gelesen und
 * nicht besprochen. Das Blatt soll 1–2 Seiten halten, also kappt jede Gruppe
 * bei MAX_PUNKTE_JE_GRUPPE — was wegfaellt, steht weiterhin im Plan, auf den
 * eingewiesen wird. Der Bearbeiter kann jederzeit nachtragen; die Kappung ist
 * ein Vorschlag, keine Sperre.
 */

// ─── Stellschrauben ─────────────────────────────────────────────────────────

/** Zielaenge eines Stichpunkts in Zeichen. Danach wird an der Wortgrenze gekappt. */
export const STICHPUNKT_MAX_ZEICHEN = 90;

/** Hoechstzahl der Stichpunkte JE GRUPPE im Vorschlag. */
export const MAX_PUNKTE_JE_GRUPPE = 8;

/** So viele Seiten darf das Blatt haben, bevor `passt` false wird. */
export const MAX_SEITEN = 2;

// ─── Stichpunkte formen ─────────────────────────────────────────────────────

/**
 * Fuellwoerter, die einen Merksatz nur laenger machen. Bewusst kurz gehalten:
 * jedes Wort hier kann auch einmal tragend sein, und ein zu eifriger Filter
 * verstuemmelt Fachtext („die Absturzsicherung ist zu pruefen" darf nicht zu
 * „Absturzsicherung zu pruefen" verkuerzt werden, wenn dabei der Sinn kippt).
 * Entfernt werden deshalb nur Floskeln, die vor dem eigentlichen Inhalt stehen
 * und nie Traeger einer Aussage sind.
 */
const FUELLWOERTER = [
  'grundsaetzlich',
  'grundsätzlich',
  'generell',
  'selbstverstaendlich',
  'selbstverständlich',
  'im rahmen',
  'im zuge',
  'gegebenenfalls',
  'ggf.',
  'ggf',
  'in jedem fall',
  'jederzeit',
  'stets',
  'immer',
  'es ist darauf zu achten, dass',
  'es ist sicherzustellen, dass',
  'darauf ist zu achten, dass',
  'es wird darauf hingewiesen, dass',
  'bitte beachten sie, dass',
  'bitte beachten, dass',
];

/**
 * Aus einem Satz einen Stichpunkt machen.
 *
 * Vier Schritte, in dieser Reihenfolge:
 *   1. Whitespace vereinheitlichen (Zeilenumbrueche aus dem Planeditor).
 *   2. Fuellwoerter vorne wegnehmen — nur am Anfang, denn nur dort sind sie
 *      verlaesslich Floskel und nicht Inhalt.
 *   3. Auf STICHPUNKT_MAX_ZEICHEN kappen, und zwar AN DER WORTGRENZE: ein
 *      mitten im Wort abgeschnittener Merksatz ist auf einem unterschriebenen
 *      Blatt schlimmer als ein etwas kuerzerer. Gekappt wird mit „…", damit
 *      erkennbar bleibt, dass etwas fehlt.
 *   4. Schlusspunkt weg. Ein Stichpunkt ist kein Satz — Fragezeichen und
 *      Ausrufezeichen bleiben dagegen stehen, die tragen Bedeutung.
 */
export const kuerzeAufStichpunkt = (
  text: string,
  maxZeichen: number = STICHPUNKT_MAX_ZEICHEN,
): string => {
  let s = (text || '').replace(/\s+/g, ' ').trim();
  if (!s) return '';

  // Fuellwoerter am Anfang, ggf. mehrere hintereinander.
  let entfernt = true;
  while (entfernt) {
    entfernt = false;
    const klein = s.toLowerCase();
    for (const w of FUELLWOERTER) {
      // Wortgrenze mitpruefen: sonst wuerde „Immerhin…" ueber das Fuellwort
      // „immer" zu „Hin…" — ein Filter, der Fachtext zerstoert, ist schlechter
      // als gar keiner.
      const grenze = klein.length === w.length || /[^a-zäöüß]/.test(klein[w.length]);
      if (klein.startsWith(w) && grenze) {
        s = s.slice(w.length).replace(/^[\s,;:]+/, '');
        // Erster Buchstabe wieder gross — sonst beginnt der Punkt klein.
        s = s.charAt(0).toUpperCase() + s.slice(1);
        entfernt = true;
        break;
      }
    }
  }

  if (s.length > maxZeichen) {
    const roh = s.slice(0, maxZeichen);
    const letzteLuecke = roh.lastIndexOf(' ');
    // Nur an der Wortgrenze schneiden, solange dabei nicht mehr als die
    // Haelfte verloren geht (ein einzelnes ueberlanges Wort hat keine Grenze).
    const kern = letzteLuecke > maxZeichen / 2 ? roh.slice(0, letzteLuecke) : roh;
    s = kern.replace(/[\s,;:.\-–]+$/, '') + '…';
  }

  // Schlusspunkt weg — aber nicht die Auslassung aus Schritt 3.
  if (s.endsWith('.') && !s.endsWith('…') && !s.endsWith('...')) {
    s = s.slice(0, -1);
  }
  return s.trim();
};

/**
 * Vergleichsform fuer die Entdopplung: klein, ohne Satzzeichen, ohne
 * Mehrfach-Leerzeichen.
 *
 * WARUM so grob: dieselbe Schutzmassnahme steht in einem gewachsenen Plan an
 * fuenf Arbeitsschritten und jedes Mal ein bisschen anders getippt
 * („Absturzsicherung ab 2,00 m." / „Absturzsicherung ab 2,00 m"). Auf dem
 * Einweisungsblatt ist das EIN Punkt.
 */
const normalisiere = (text: string): string =>
  (text || '')
    .toLowerCase()
    .replace(/[.,;:!?„“"'()\[\]–—-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Sammelstelle einer Gruppe: kuerzt, entdoppelt und kappt in einem Zug.
 *
 * Die Reihenfolge ist wichtig — entdoppelt wird NACH dem Kuerzen: zwei Saetze,
 * die sich erst hinter Zeichen 90 unterscheiden, sind auf dem Blatt derselbe
 * Punkt und duerfen nicht zweimal dastehen.
 */
class Punktesammler {
  private readonly gesehen = new Set<string>();
  private readonly punkte: string[] = [];

  constructor(private readonly maxPunkte: number = MAX_PUNKTE_JE_GRUPPE) {}

  add(text: string | null | undefined): void {
    if (this.punkte.length >= this.maxPunkte) return;
    const kurz = kuerzeAufStichpunkt(text || '');
    if (!kurz) return;
    const key = normalisiere(kurz);
    if (!key || this.gesehen.has(key)) return;
    this.gesehen.add(key);
    this.punkte.push(kurz);
  }

  addAlle(texte: Iterable<string | null | undefined>): void {
    for (const t of texte) this.add(t);
  }

  get laenge(): number {
    return this.punkte.length;
  }

  ergebnis(): string[] {
    return [...this.punkte];
  }
}

// ─── Fachliche Standardtexte ────────────────────────────────────────────────

/**
 * Punkt 5 des Blattes. Fester Text, weil es hier nichts aus dem Plan zu holen
 * gibt: die Pflichten der Unternehmer stehen in der Baustellenverordnung
 * (§ 5 BaustellV) und in § 8 ArbSchG und sind bei jeder Baustelle dieselben.
 */
const PFLICHTEN_UNTERNEHMER = [
  'Der SiGe-Plan ist für alle tätigen Unternehmen verbindlich (§ 5 BaustellV)',
  'Gefährdungsbeurteilung und Unterweisung der eigenen Leute bleiben Sache des Unternehmers',
  'Änderungen im Bauablauf sind dem SiGeKo unverzüglich zu melden',
  'Arbeiten mehrerer Gewerke am selben Ort sind vorher abzustimmen (§ 8 ArbSchG)',
  'Weisungen des SiGeKo zur Koordinierung sind zu befolgen',
];

/** Grundpunkte zum Aufbau, die keinen Planinhalt brauchen. */
const AUFBAU_GRUNDTEXT = [
  'Der SiGe-Plan hängt an der Baustelle aus und ist für jeden Beschäftigten einsehbar',
  'Aufbau: je Arbeitsschritt eine Zeile mit Schutzmaßnahmen, Vorschriften, Zuständigkeit',
  'Der Zeitbalken zeigt, wann welcher Schritt läuft und wo Gewerke aufeinandertreffen',
];

/** Notrufpunkt, der immer gilt — auch wenn kein Notrufplan gepflegt ist. */
const NOTRUF_GRUNDTEXT = 'Notruf 112 (Rettungsdienst und Feuerwehr), Polizei 110';

// ─── Der Vorschlag ──────────────────────────────────────────────────────────

/** Titel der fünf Gruppen. Der Schlüssel bleibt stabil, der Titel ist Anzeige. */
export const GRUPPEN_TITEL: Record<string, string> = {
  aufbau: 'Aufbau des SiGe-Plans',
  gefahren: 'Wesentliche Gefahren auf dieser Baustelle',
  regeln: 'Regeln und Schutzmaßnahmen',
  notfall: 'Notfall, Erste Hilfe, Rettungskette',
  pflichten: 'Pflichten der Unternehmer',
};

const legendeAusBlock = (block: SigeBlock | undefined): LegendeEintrag[] => {
  const eintraege = (block?.inhalt as { eintraege?: LegendeEintrag[] } | undefined)?.eintraege;
  return Array.isArray(eintraege) ? eintraege : [];
};

const piktoLabels = (block: SigeBlock | undefined): string[] => {
  const icons = (block?.inhalt as { icons?: string[] } | undefined)?.icons;
  if (!Array.isArray(icons)) return [];
  return icons
    .map((i) => aufloesePikto(i)?.label)
    .filter((l): l is string => !!l);
};

const zeilenAusBlock = (block: SigeBlock | undefined): string[] => {
  const zeilen = (block?.inhalt as { zeilen?: string[] } | undefined)?.zeilen;
  return Array.isArray(zeilen) ? zeilen : [];
};

const kontaktZeile = (rolle: string, k?: { name?: string; adresse?: string; telefon?: string }): string | null => {
  if (!k?.name?.trim()) return null;
  const teile = [k.name.trim()];
  if (k.telefon?.trim()) teile.push(`Tel. ${k.telefon.trim()}`);
  return `${rolle}: ${teile.join(', ')}`;
};

/**
 * Was die Baustellenordnung des SiGe-Handbuchs zur Einweisung beisteuert.
 *
 * Bewusst NUR das Feld, das hier gebraucht wird, und bewusst KEIN Import aus
 * `components/handbuch/types.ts`: `Baustellenordnung` (dort :74) ist auf diese
 * Form strukturell zuweisbar, aber dieser Dienst soll nicht davon abhaengen,
 * ob ein Handbuchmodul existiert. Fehlt das Handbuch — und es fehlt bei jedem
 * Bestandsprojekt —, bleibt die Einweisung vollstaendig.
 */
export interface EinweisungOrdnung {
  regeln?: string[] | null;
}

/**
 * DER Vorschlag: fünf Gruppen aus dem Plan, fertig als Stichpunkte.
 *
 * Leere Gruppen fallen heraus. Das ist Absicht: eine Überschrift „Regeln und
 * Schutzmaßnahmen" ohne einen einzigen Punkt darunter sieht auf dem
 * unterschriebenen Blatt aus wie ein Fehler beim Drucken. Gruppen mit
 * fachlichem Standardtext (Aufbau, Notfall, Pflichten) sind deshalb nie leer,
 * auch nicht bei einem frisch angelegten Plan.
 *
 * Alle Argumente sind tolerant gegen `undefined` — der Aufrufer lädt die
 * Kinderbäume einzeln, und ein noch nicht geladener Zweig darf keinen Vorschlag
 * verhindern.
 *
 * `ordnung` ist der einzige Zugang von außerhalb des Plans: die
 * Baustellenordnung des SiGe-Handbuchs. Sie ist OPTIONAL und ändert ohne
 * Übergabe gar nichts — ein Projekt ohne Handbuch bekommt denselben Vorschlag
 * wie bisher, Zeichen für Zeichen.
 */
export const buildEinweisungVorschlag = (
  sigeProject: SigeProject | null | undefined,
  arbeitsschritte: Arbeitsschritt[] = [],
  gefaehrdungen: Gefaehrdung[] = [],
  massnahmen: Schutzmassnahme[] = [],
  bloecke: SigeBlock[] = [],
  ordnung?: EinweisungOrdnung | null,
): EinweisungGruppe[] => {
  const schritte = arbeitsschritte ?? [];
  const gefahren = gefaehrdungen ?? [];
  const schutz = massnahmen ?? [];
  const blockliste = bloecke ?? [];

  // ── 1) Aufbau des SiGe-Plans ─────────────────────────────────────────────
  const aufbau = new Punktesammler();

  const planNr = sigeProject?.plan_nr?.trim();
  const planStand = sigeProject?.plan_datum?.trim();
  if (planNr || planStand) {
    const teile = [];
    if (planNr) teile.push(`Plan Nr. ${planNr}`);
    if (planStand) teile.push(`Stand ${planStand}`);
    aufbau.add(`Eingewiesen wird in: ${teile.join(', ')}`);
  }

  // Hauptabschnitte: die im Plan TATSAECHLICH belegten, in Planreihenfolge.
  // Bewusst EIN Punkt statt einer je Abschnitt — zehn Abschnitte fraessen die
  // ganze Gruppe, und die Gliederung ist eine einzige Aussage.
  const abschnitteImPlan = (sigeProject?.hauptabschnitte?.length
    ? sigeProject.hauptabschnitte
    : [...new Set(schritte.map((s) => s.hauptabschnitt).filter((h): h is string => !!h?.trim()))]);
  if (abschnitteImPlan.length > 0) {
    aufbau.add(
      `Gliederung in ${abschnitteImPlan.length} Hauptabschnitte: ${abschnitteImPlan.join(', ')}`,
    );
  }

  aufbau.addAlle(AUFBAU_GRUNDTEXT);

  const legende = legendeAusBlock(blockliste.find((b) => b.typ === 'legende'));
  if (legende.length > 0) {
    aufbau.add(
      `Legende: ${legende
        .filter((e) => e?.kuerzel)
        .map((e) => `${e.kuerzel} = ${e.bedeutung}`)
        .join(', ')}`,
    );
  }

  const pikto = piktoLabels(blockliste.find((b) => b.typ === 'piktogrammfeld'));
  if (pikto.length > 0) {
    aufbau.add(`Piktogramme auf dem Plan: ${pikto.join(', ')}`);
  }

  // ── 2) Wesentliche Gefahren ──────────────────────────────────────────────
  // Zwei Quellen, in dieser Reihenfolge: als KRITISCH markierte Arbeitsschritte
  // (die ausdrueckliche Aussage eines Menschen) und danach die Gefaehrdungen
  // mit Prioritaet 'hoch'. Die Markierung schlaegt die Priorisierung, weil sie
  // die Baustelle meint und nicht den Katalog.
  const gefahrenGruppe = new Punktesammler();

  const schrittNachId = new Map(schritte.map((s) => [s.id, s]));

  for (const s of schritte) {
    if (s.kritisch !== true) continue;
    const gewerk = s.gewerk?.trim();
    gefahrenGruppe.add(gewerk ? `${s.bezeichnung} (${gewerk})` : s.bezeichnung);
  }

  for (const g of gefahren) {
    if (g.prioritaet !== 'hoch') continue;
    const schritt = schrittNachId.get(g.arbeitsschritt_id);
    gefahrenGruppe.add(
      schritt?.bezeichnung ? `${g.bezeichnung} — bei: ${schritt.bezeichnung}` : g.bezeichnung,
    );
  }

  if (gefahrenGruppe.laenge === 0) {
    // Kein kritischer Schritt und keine hohe Gefaehrdung ist eine AUSSAGE und
    // kein Loch. Ohne diesen Satz sieht das Blatt aus, als sei die Gruppe beim
    // Erzeugen vergessen worden.
    gefahrenGruppe.add(
      'Kein Arbeitsschritt ist als kritisch markiert — es gelten die allgemeinen Regeln',
    );
  }

  // ── 3) Regeln und Schutzmaßnahmen ────────────────────────────────────────
  // Beide Speicherorte zusammen: die Freitextliste am Arbeitsschritt
  // (`schutzmassnahmen`) und die zur Gefaehrdung erfassten Massnahmen. Ein
  // gewachsener Plan nutzt beide, und derselbe Satz steht regelmaessig in
  // beiden — deshalb ist die Entdopplung hier der eigentliche Kern.
  //
  // Die Baustellenordnung steht VORNE, wenn es sie gibt. Sie ist der Text, den
  // der SiGeKo für genau diese Baustelle geschrieben hat und über den auf der
  // Baustelle auch gesprochen wird ("Helmpflicht auf dem ganzen Gelände"). Die
  // Schutzmaßnahmen des Plans sind gewachsener Katalogtext und stehen ohnehin
  // im Plan, auf den eingewiesen wird — sie sind es, die bei MAX_PUNKTE_JE_
  // GRUPPE hinten abfallen dürfen, nicht die Hausordnung.
  const regeln = new Punktesammler();
  regeln.addAlle(ordnung?.regeln ?? []);
  regeln.addAlle(schutz.map((m) => m.beschreibung));
  for (const s of schritte) {
    regeln.addAlle(s.schutzmassnahmen ?? []);
  }

  // ── 4) Notfall ───────────────────────────────────────────────────────────
  const notfall = new Punktesammler();
  notfall.add(NOTRUF_GRUNDTEXT);

  const notrufBlock = blockliste.find((b) => b.typ === 'notrufplan');
  const notruf = (notrufBlock?.inhalt ?? {}) as NotrufplanInhalt;

  notfall.add(kontaktZeile('Krankenhaus', notruf.krankenhaus));
  notfall.add(kontaktZeile('Durchgangsarzt', notruf.durchgangsarzt));
  notfall.add(kontaktZeile('Augenarzt', notruf.augenarzt));
  notfall.add(kontaktZeile('Apotheke', notruf.apotheke));

  // Ersthelfer stehen je nach Planalter im Notrufplan-Block ODER in einem
  // eigenen Block vom Typ 'ersthelfer'. Beide Wege lesen, entdoppelt wird
  // ohnehin.
  const ersthelfer = [
    ...(notruf.ersthelfer ?? []),
    ...(((blockliste.find((b) => b.typ === 'ersthelfer')?.inhalt as
      | { ersthelfer?: { name: string; firma?: string }[] }
      | undefined)?.ersthelfer) ?? []),
  ].filter((e) => e?.name?.trim());
  if (ersthelfer.length > 0) {
    notfall.add(
      `Ersthelfer: ${ersthelfer
        .map((e) => (e.firma?.trim() ? `${e.name} (${e.firma})` : e.name))
        .join(', ')}`,
    );
  }

  notfall.addAlle(zeilenAusBlock(blockliste.find((b) => b.typ === 'notrufnummern')));

  // ── 5) Pflichten der Unternehmer ─────────────────────────────────────────
  const pflichten = new Punktesammler();
  pflichten.addAlle(PFLICHTEN_UNTERNEHMER);

  const gruppen: EinweisungGruppe[] = [
    { id: 'aufbau', titel: GRUPPEN_TITEL.aufbau, punkte: aufbau.ergebnis() },
    { id: 'gefahren', titel: GRUPPEN_TITEL.gefahren, punkte: gefahrenGruppe.ergebnis() },
    { id: 'regeln', titel: GRUPPEN_TITEL.regeln, punkte: regeln.ergebnis() },
    { id: 'notfall', titel: GRUPPEN_TITEL.notfall, punkte: notfall.ergebnis() },
    { id: 'pflichten', titel: GRUPPEN_TITEL.pflichten, punkte: pflichten.ergebnis() },
  ];

  return gruppen.filter((g) => g.punkte.length > 0);
};

// ─── Seitenschätzung ────────────────────────────────────────────────────────

/**
 * Zeilen, die auf eine A4-Seite passen — Blatt mit Rändern, 10 pt, 4,6 mm
 * Zeilenabstand. Bewusst konservativ.
 */
const ZEILEN_JE_SEITE = 46;

/** Zeichen, die in eine Zeile passen (10 pt Helvetica auf 170 mm Satzspiegel). */
const ZEICHEN_JE_ZEILE = 78;

/** Kopfbereich: Titel, Projekt, Datum/Uhrzeit/Ort/Dauer, Durchführender, Planbezug. */
const ZEILEN_KOPF = 8;

/** Überschrift einer Gruppe plus Luft davor. */
const ZEILEN_JE_GRUPPE = 2;

/** Überschrift der Unterschriftenliste plus ihre Kopfzeile. */
const ZEILEN_LISTENKOPF = 4;

/** Eine Unterschriftszeile ist doppelt so hoch wie eine Textzeile. */
const ZEILEN_JE_UNTERSCHRIFT = 2;

export interface SeitenSchaetzung {
  /**
   * Geschätzte Seiten des INHALTS — ohne die Unterschriftenliste, mindestens 1.
   *
   * Dieselbe Zählweise wie `zaehleEinweisungSeiten` im PDF-Generator, und das
   * ist der Punkt: begrenzt sind die 1–2 Seiten Inhalt, die Unterschriften
   * kommen ausdrücklich obendrauf. Zählte man sie hier mit, meldete der Editor
   * bei 20 Teilnehmern „zu viel", während das fertige PDF einen sauberen
   * Einseiter zeigt.
   */
  seitenInhalt: number;
  /** Geschätzte Seiten des ganzen Blattes, Unterschriftenliste eingerechnet. */
  seitenGesamt: number;
  /** Hält der INHALT die angestrebten MAX_SEITEN Seiten ein? */
  passt: boolean;
  /** Inhaltszeilen, die über MAX_SEITEN hinausragen. 0, wenn alles passt. */
  ueberhang: number;
}

/**
 * Ehrliche Schätzung für die Live-Anzeige neben dem Editor („passt auf
 * 2 Seiten" / „1 Punkt zu viel").
 *
 * AUSDRÜCKLICH eine Schätzung: die verbindliche Seitenzahl liefert erst der
 * PDF-Generator, der die tatsächliche Textbreite misst. Diese Funktion darf
 * deshalb NIE etwas sperren — sie färbt eine Anzeige, mehr nicht. Sie rechnet
 * bewusst etwas großzügig (78 Zeichen je Zeile statt der real möglichen ~95),
 * damit die Anzeige eher zu früh als zu spät warnt: ein Blatt, das laut
 * Anzeige passt und dann doch dreiseitig aus dem Drucker kommt, ist der
 * ärgerlichere Fehler.
 */
export const schaetzeSeiten = (
  gruppen: EinweisungGruppe[],
  teilnehmerAnzahl: number,
  leerzeilen: number,
): SeitenSchaetzung => {
  const liste = gruppen ?? [];
  const teilnehmer = Math.max(0, Math.floor(teilnehmerAnzahl || 0));
  const blanko = Math.max(0, Math.floor(leerzeilen || 0));

  const inhaltszeilen = liste.reduce((summe, gruppe) => {
    const punkte = (gruppe.punkte ?? []).reduce(
      (acc, p) => acc + Math.max(1, Math.ceil((p || '').length / ZEICHEN_JE_ZEILE)),
      0,
    );
    return summe + ZEILEN_JE_GRUPPE + punkte;
  }, 0);

  const unterschriften =
    ZEILEN_LISTENKOPF + (teilnehmer + blanko) * ZEILEN_JE_UNTERSCHRIFT;

  // Nur der Inhalt zählt gegen das Budget — die Unterschriftenliste beginnt im
  // PDF ohnehin auf einer eigenen Seite und ist nicht begrenzt.
  const inhaltGesamt = ZEILEN_KOPF + inhaltszeilen;
  const seitenInhalt = Math.max(1, Math.ceil(inhaltGesamt / ZEILEN_JE_SEITE));
  const platz = MAX_SEITEN * ZEILEN_JE_SEITE;

  return {
    seitenInhalt,
    seitenGesamt:
      seitenInhalt + Math.max(1, Math.ceil(unterschriften / ZEILEN_JE_SEITE)),
    passt: seitenInhalt <= MAX_SEITEN,
    ueberhang: Math.max(0, inhaltGesamt - platz),
  };
};

// ─── Laufende Nummer ────────────────────────────────────────────────────────

/**
 * Die nächste laufende Nummer je Projekt.
 *
 * HÖCHSTE vergebene Nummer + 1, Lücken werden NICHT gefüllt. Eine gelöschte
 * Einweisung Nr. 3 bleibt eine Lücke: würde die nächste sie auffüllen, trüge
 * ein neues Blatt dieselbe Nummer wie ein bereits unterschriebenes und
 * verschicktes — die laufende Nummer ist ein Aktenzeichen, kein Zähler.
 *
 * Dieselbe Regel wie bei den Begehungseinträgen (`ticket_number`). Und wie
 * dort gilt: die Nummer wird unmittelbar vor dem Insert bestimmt, NIE vor
 * einem `await` gemerkt und später geschrieben — genau das hat im Repo schon
 * einmal einen Eintrag überschrieben (siehe sigeEinweisungService.ts).
 */
export const naechsteEinweisungsNummer = (
  bestehende: { nummer: number }[] | null | undefined,
): number => {
  const nummern = (bestehende ?? [])
    .map((e) => Number(e?.nummer))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (nummern.length === 0) return 1;
  return Math.max(...nummern) + 1;
};
